import React, { Suspense, lazy, useCallback } from 'react';
import PropTypes from 'prop-types';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import ThemeProvider from 'theme/ThemeProvider';
import config from 'config';
import { Spinner } from 'components/elements';
import { routes, roles } from 'const';
import { toastMessage } from 'utils';
import { useAuth } from 'hooks';
import BaseLayout from 'components/layouts/base';
import PortalLayout from 'components/layouts/portal';
import SettingsLayout from 'components/layouts/settings';
import VideoProvider from 'components/providers/VideoProvider';
import ModalProvider from 'components/providers/ModalProvider';
import StripeProvider from 'components/providers/StripeProvider';
import IntercomProvider from 'components/providers/IntercomProvider';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';

const Booking = lazy(() => import('pages/Booking/Index'));
const ThankYou = lazy(() => import('pages/ThankYou/ThankYou'));
const Login = lazy(() => import('pages/Login/Login'));
const PasswordRecovery = lazy(() => import('pages/PasswordRecovery/PasswordRecovery'));
const PasswordReset = lazy(() => import('pages/PasswordReset/PasswordReset'));
const AccountActivation = lazy(() => import('pages/AccountActivation/AccountActivation'));
const Appointments = lazy(() => import('pages/Appointments'));
const Invoices = lazy(() => import('pages/Invoices'));
const AppointmentDetail = lazy(() => import('pages/AppointmentDetail'));
const Dashboard = lazy(() => import('pages/Dashboard'));
const Messages = lazy(() => import('pages/Messages'));
const Profile = lazy(() => import('pages/Profile'));
const Payment = lazy(() => import('pages/Payment'));
const DevTools = lazy(() => import('pages/Dev/DevTools'));
const UserSettings = lazy(() => import('pages/UserSettings'));
const UserAccount = lazy(() => import('pages/UserAccount'));
const SessionAccess = lazy(() => import('pages/SessionAccess/SessionAccess'));
const VideoSession = lazy(() => import('pages/VideoSession'));
const TermsAndCondition = lazy(() => import('pages/TermsAndCondition'));

function VideoContext({ children }) {
  const showError = useCallback((header, message) => toastMessage(header, message));
  return <VideoProvider onError={showError}>{children}</VideoProvider>;
}

VideoContext.propTypes = {
  children: PropTypes.element.isRequired,
};

function Routs() {
  const { user } = useAuth();
  return (
    <Router>
      <Suspense fallback={<Spinner />}>
        <React.StrictMode>
          <ThemeProvider>
            <IntercomProvider>
              <BaseLayout>
                <ModalProvider>
                  <CssBaseline />
                  <Switch>
                    {config.isDevelopment && <Route exact path="/dev-tools" component={DevTools} />}
                    <PublicRoute authenticatedOnly={false} path={routes.login} redirectTo={routes.appointments}>
                      <Route exact path={routes.login} component={Login} />
                    </PublicRoute>
                    <Route exact path={`${routes.sessionAccess}/:id`}>
                      <SessionAccess context={VideoContext} />
                    </Route>
                    <Route exact path={routes.termsandcondition} component={TermsAndCondition} />
                    <Route exact path={routes.passwordRecovery} component={PasswordRecovery} />
                    <Route exact path={routes.passwordReset} component={PasswordReset} />
                    <Route exact path={routes.accountActivation} component={AccountActivation} />
                    <Route exact path={routes.unauthenticatedBooking}>
                      <StripeProvider>
                        <Booking />
                      </StripeProvider>
                    </Route>
                    <Route exact path={routes.unauthenticatedBookingThankYou} component={ThankYou} />
                    <Route exact path={`${routes.videoSession}/:id`}>
                      <PrivateRoute authenticatedOnly path={`${routes.videoSession}/:id`} redirectTo={routes.login} role={roles.patient}>
                        <VideoContext>
                          <VideoSession />
                        </VideoContext>
                      </PrivateRoute>
                    </Route>
                    <PrivateRoute authenticatedOnly path={routes.portal} redirectTo={routes.login} role={roles.patient}>
                      <PortalLayout>
                        <Route path={`${routes.messages}/:id?`} component={Messages} />
                        <Route exact path={routes.dashboard} component={Dashboard} />
                        <Route exact path={routes.profile} component={Profile} />
                        <Route exact path={routes.appointments} component={Appointments} />
                        <Route exact path={`${routes.appointments}/:id`} component={AppointmentDetail} />
                        <Route exact path={routes.invoices} component={Invoices} />
                        <Route path={routes.settings}>
                          <SettingsLayout>
                            <Route exact path={routes.settings} component={UserSettings} />
                            <Route path={routes.account} component={UserAccount} />
                            <Route path={routes.payment}>
                              <StripeProvider>
                                <Payment />
                              </StripeProvider>
                            </Route>
                          </SettingsLayout>
                        </Route>
                      </PortalLayout>
                      {user?.termsConsented === false && <Redirect to={routes.termsandcondition} />}
                      <Route exact path={routes.portal}>
                        <Redirect to={routes.appointments} />
                      </Route>
                    </PrivateRoute>
                    <Route path="*">
                      <Redirect to={routes.portal} />
                    </Route>
                  </Switch>
                </ModalProvider>
              </BaseLayout>
            </IntercomProvider>
          </ThemeProvider>
        </React.StrictMode>
      </Suspense>
    </Router>
  );
}

export default Routs;
